import isEqual from "lodash/isEqual";

import {
    collectExecutiveSummaryRowKeys,
    ensureExecutiveSummaryBandIds,
    getCombBandChildren,
    getExecutiveSummaryBandLabel,
    isExecutiveSummaryCombBand,
    normalizeExecutiveBandForEditor,
    normalizeExecutiveSummaryForCompare
} from "./executiveSummaryUtils";

function parseExecutiveSummaryValue(raw) {
    if (raw == null || raw === "") return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    }
    return [];
}

/** Parse `[before, after]` executive_summary values from a log changeset. */
export function parseExecutiveSummaryLogChangeset(values) {
    if (!Array.isArray(values) || values.length < 2) {
        throw new Error("Invalid executive summary changeset");
    }
    try {
        return {
            beforeBands: parseExecutiveSummaryValue(values[0]),
            afterBands: parseExecutiveSummaryValue(values[1])
        };
    } catch {
        throw new Error("Could not parse executive summary for this log entry");
    }
}

/** Normalize log bands for read-only display in ExecutiveSummaryBandsPanel. */
export function normalizeExecutiveSummaryBandsForDisplay(bands, scopeId = "log") {
    const rawBands = Array.isArray(bands) ? bands : [];
    const idByKey = new Map();
    return ensureExecutiveSummaryBandIds(
        rawBands.map((band, index) => normalizeExecutiveBandForEditor(band, scopeId, index, idByKey))
    );
}

function getBandDisplayLabel(band) {
    return getExecutiveSummaryBandLabel(band) || band?.key || band?.id || "Unnamed band";
}

function getBandIdentityFallback(band, pathLabel) {
    const label = getBandDisplayLabel(band);
    const type = band?.type ?? "band";
    if (label && label !== "Unnamed band") return `${type}:${label}`;
    return pathLabel;
}

function getBandIdentity(band, pathLabel) {
    if (band?.id != null && String(band.id) !== "") return String(band.id);
    if (band?.key != null && String(band.key) !== "") return String(band.key);
    return getBandIdentityFallback(band, pathLabel);
}

function mapTopLevelBands(bands) {
    const map = new Map();
    (Array.isArray(bands) ? bands : []).forEach((band, index) => {
        if (!band || typeof band !== "object") return;
        const identity = getBandIdentity(band, `top-${index}`);
        map.set(identity, { band, label: getBandDisplayLabel(band) });
    });
    return map;
}

function bandContentEqual(beforeBand, afterBand) {
    if (!beforeBand && !afterBand) return true;
    if (!beforeBand || !afterBand) return false;
    return isEqual(
        normalizeExecutiveSummaryForCompare([beforeBand])[0],
        normalizeExecutiveSummaryForCompare([afterBand])[0]
    );
}

function stableBandSortKey(band) {
    if (!band || typeof band !== "object") return "";
    if (band.id != null && String(band.id) !== "") return String(band.id);
    if (band.key != null && String(band.key) !== "") return String(band.key);
    return String(band.label ?? band.key ?? "");
}

function normalizeBandNormIgnoringChildOrder(normBand) {
    if (!normBand || typeof normBand !== "object") return {};
    if (normBand.type !== "comb_band" || !Array.isArray(normBand.bands)) return normBand;

    return {
        ...normBand,
        bands: [...normBand.bands]
            .sort((a, b) => stableBandSortKey(a).localeCompare(stableBandSortKey(b)))
            .map(child => (child?.type === "comb_band" ? normalizeBandNormIgnoringChildOrder(child) : child))
    };
}

function bandContentEqualIgnoringChildOrder(beforeBand, afterBand) {
    if (!beforeBand && !afterBand) return true;
    if (!beforeBand || !afterBand) return false;
    const beforeNorm = normalizeBandNormIgnoringChildOrder(normalizeExecutiveSummaryForCompare([beforeBand])[0]);
    const afterNorm = normalizeBandNormIgnoringChildOrder(normalizeExecutiveSummaryForCompare([afterBand])[0]);
    return isEqual(beforeNorm, afterNorm);
}

function isCombChildOrderOnlyChange(beforeBand, afterBand) {
    if (!beforeBand || !afterBand) return false;
    if (!isExecutiveSummaryCombBand(beforeBand) || !isExecutiveSummaryCombBand(afterBand)) return false;
    if (bandContentEqual(beforeBand, afterBand)) return false;
    return bandContentEqualIgnoringChildOrder(beforeBand, afterBand);
}

function getTopLevelBandIdentitySequence(bands) {
    return (Array.isArray(bands) ? bands : [])
        .filter(band => band && typeof band === "object")
        .map((band, index) => getBandIdentity(band, `top-${index}`));
}

/** True when the same top-level bands appear in a different order (matched by id/key). */
export function detectTopLevelBandOrderChanged(beforeBands, afterBands) {
    const beforeSeq = getTopLevelBandIdentitySequence(beforeBands);
    const afterSeq = getTopLevelBandIdentitySequence(afterBands);
    if (!beforeSeq.length || beforeSeq.length !== afterSeq.length) return false;

    const afterSet = new Set(afterSeq);
    if (beforeSeq.some(identity => !afterSet.has(identity))) return false;

    return beforeSeq.some((identity, index) => identity !== afterSeq[index]);
}

/** Flatten nested bands for child-level filter inside comb groups. */
function flattenExecutiveSummaryBandsForDiff(bands, parentPrefix = "") {
    const out = [];
    (Array.isArray(bands) ? bands : []).forEach(band => {
        if (!band || typeof band !== "object") return;
        const label = getBandDisplayLabel(band);
        const pathLabel = parentPrefix ? `${parentPrefix} › ${label}` : label;
        const identity = getBandIdentity(band, pathLabel);
        out.push({ band, identity, label: pathLabel });
        if (isExecutiveSummaryCombBand(band)) {
            flattenExecutiveSummaryBandsForDiff(getCombBandChildren(band), pathLabel).forEach(child => out.push(child));
        }
    });
    return out;
}

function getNestedChangedIdentities(beforeBands, afterBands) {
    const beforeFlat = flattenExecutiveSummaryBandsForDiff(beforeBands);
    const afterFlat = flattenExecutiveSummaryBandsForDiff(afterBands);
    const beforeById = new Map(beforeFlat.map(entry => [entry.identity, entry]));
    const afterById = new Map(afterFlat.map(entry => [entry.identity, entry]));
    const changed = new Set();

    afterFlat.forEach(({ identity, band }) => {
        const prev = beforeById.get(identity);
        if (!prev) {
            changed.add(identity);
            return;
        }
        if (!bandContentEqual(prev.band, band)) {
            changed.add(identity);
        }
    });

    beforeFlat.forEach(({ identity }) => {
        if (!afterById.has(identity)) {
            changed.add(identity);
        }
    });

    return changed;
}

/** Collect identities used to filter the compare tree (top-level + nested comb children). */
export function getChangedExecutiveSummaryBandIdentities(beforeBands, afterBands) {
    const changed = new Set(getNestedChangedIdentities(beforeBands, afterBands));
    const beforeTop = mapTopLevelBands(beforeBands);
    const afterTop = mapTopLevelBands(afterBands);
    const topIdentities = new Set([...beforeTop.keys(), ...afterTop.keys()]);

    topIdentities.forEach(identity => {
        const beforeEntry = beforeTop.get(identity);
        const afterEntry = afterTop.get(identity);
        if (!beforeEntry || !afterEntry) {
            changed.add(identity);
            return;
        }
        if (!bandContentEqual(beforeEntry.band, afterEntry.band)) {
            changed.add(identity);
        }
    });

    return changed;
}

function collectBandsByFilterIdentity(bands, parentPrefix = "", map = new Map()) {
    (Array.isArray(bands) ? bands : []).forEach((band, index) => {
        if (!band || typeof band !== "object") return;
        const label = getBandDisplayLabel(band);
        const pathLabel = parentPrefix ? `${parentPrefix} › ${label}` : label;
        const identity = getBandIdentity(band, parentPrefix ? pathLabel : `top-${index}`);
        if (!map.has(identity)) map.set(identity, band);
        if (isExecutiveSummaryCombBand(band)) {
            collectBandsByFilterIdentity(getCombBandChildren(band), pathLabel, map);
        }
    });
    return map;
}

function combBandOwnFieldsEqual(beforeBand, afterBand) {
    const ownFields = band => {
        const norm = normalizeExecutiveSummaryForCompare([band])[0];
        if (!norm || typeof norm !== "object") return norm;
        const { bands: _children, ...rest } = norm;
        return rest;
    };
    return isEqual(ownFields(beforeBand), ownFields(afterBand));
}

function filterBandsTreeToChangedOnly(bands, changedIdentities, counterpartByIdentity, parentPrefix = "") {
    if (!Array.isArray(bands) || !changedIdentities?.size) return [];

    return bands
        .map((band, index) => {
            if (!band || typeof band !== "object") return null;

            const label = getBandDisplayLabel(band);
            const pathLabel = parentPrefix ? `${parentPrefix} › ${label}` : label;
            const identity = getBandIdentity(band, parentPrefix ? pathLabel : `top-${index}`);
            const combBand = isExecutiveSummaryCombBand(band);

            if (combBand) {
                const counterpart = counterpartByIdentity?.get(identity);
                if (!counterpart) return band;

                if (detectTopLevelBandOrderChanged(getCombBandChildren(counterpart), getCombBandChildren(band))) {
                    return band;
                }

                const children = filterBandsTreeToChangedOnly(
                    getCombBandChildren(band),
                    changedIdentities,
                    counterpartByIdentity,
                    pathLabel
                );
                if (children.length) return { ...band, bands: children };
                if (!combBandOwnFieldsEqual(counterpart, band)) return { ...band, bands: [] };
                return null;
            }

            return changedIdentities.has(identity) ? band : null;
        })
        .filter(Boolean);
}

/** Keep only bands that differ between before and after (both sides). */
export function filterExecutiveSummaryBandsToChangedOnly(beforeBands, afterBands) {
    const changedIdentities = getChangedExecutiveSummaryBandIdentities(beforeBands, afterBands);
    const beforeByIdentity = collectBandsByFilterIdentity(beforeBands);
    const afterByIdentity = collectBandsByFilterIdentity(afterBands);
    return {
        beforeBands: filterBandsTreeToChangedOnly(beforeBands, changedIdentities, afterByIdentity),
        afterBands: filterBandsTreeToChangedOnly(afterBands, changedIdentities, beforeByIdentity),
        changedIdentities
    };
}

/** Selective-restore key: the band label, not its id — the compare panel re-normalizes ids. */
export function executiveSummaryBandSelectionKey(band) {
    return String(band?.label ?? band?.key ?? "");
}

function mergeSelectedDraftLevel(savedLevel, draftLevel, selectedKeys) {
    const savedList = Array.isArray(savedLevel) ? savedLevel : [];
    const draftList = Array.isArray(draftLevel) ? draftLevel : [];

    const draftByKey = new Map();
    draftList.forEach((band, index) => {
        if (!band || typeof band !== "object") return;
        draftByKey.set(executiveSummaryBandSelectionKey(band), { band, index });
    });

    const out = savedList.map((savedBand, index) => {
        if (!savedBand || typeof savedBand !== "object") return savedBand;
        const key = executiveSummaryBandSelectionKey(savedBand);
        const draftEntry = draftByKey.get(key);
        if (!draftEntry) return savedBand;

        if (selectedKeys.has(key)) return draftEntry.band;

        if (isExecutiveSummaryCombBand(savedBand) && isExecutiveSummaryCombBand(draftEntry.band)) {
            const children = mergeSelectedDraftLevel(
                getCombBandChildren(savedBand),
                getCombBandChildren(draftEntry.band),
                selectedKeys
            );
            return { ...savedBand, bands: children };
        }
        return savedBand;
    });

    const savedKeys = new Set(
        savedList.filter(band => band && typeof band === "object").map(executiveSummaryBandSelectionKey)
    );
    draftList.forEach(band => {
        if (!band || typeof band !== "object") return;
        const key = executiveSummaryBandSelectionKey(band);
        if (!savedKeys.has(key) && selectedKeys.has(key)) out.push(band);
    });

    return out;
}

export function mergeSelectedDraftBands(savedBands, draftBands, selectedKeys) {
    const keys = selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys || []);
    if (!keys.size) return Array.isArray(savedBands) ? savedBands : [];
    return mergeSelectedDraftLevel(savedBands, draftBands, keys);
}

const MODIFIED_CHILDREN_SHOWN = 3;

function collectChangedChildPaths(beforeBand, afterBand) {
    const beforeChildren = mapTopLevelBands(getCombBandChildren(beforeBand));
    const afterChildren = mapTopLevelBands(getCombBandChildren(afterBand));
    const identities = new Set([...beforeChildren.keys(), ...afterChildren.keys()]);
    const paths = [];

    identities.forEach(identity => {
        const before = beforeChildren.get(identity);
        const after = afterChildren.get(identity);
        if (!before || !after) {
            paths.push((after || before).label);
            return;
        }
        if (bandContentEqual(before.band, after.band)) return;

        if (
            isExecutiveSummaryCombBand(before.band) &&
            isExecutiveSummaryCombBand(after.band) &&
            combBandOwnFieldsEqual(before.band, after.band)
        ) {
            const nested = collectChangedChildPaths(before.band, after.band);
            if (nested.length) {
                nested.forEach(path => paths.push(`${after.label} › ${path}`));
                return;
            }
        }
        paths.push(after.label || before.label);
    });

    return paths;
}

function describeModifiedBand(beforeBand, afterBand, label) {
    if (!isExecutiveSummaryCombBand(beforeBand) || !isExecutiveSummaryCombBand(afterBand)) return label;
    if (!combBandOwnFieldsEqual(beforeBand, afterBand)) return label;

    const paths = collectChangedChildPaths(beforeBand, afterBand);
    if (!paths.length) return label;

    const shown = paths.slice(0, MODIFIED_CHILDREN_SHOWN).join(", ");
    const rest = paths.length - MODIFIED_CHILDREN_SHOWN;
    return `${label} › ${shown}${rest > 0 ? ` +${rest} more` : ""}`;
}

/** Summarize added / removed / modified / reordered top-level band labels. */
export function summarizeExecutiveSummaryLogDiff(beforeBands, afterBands) {
    const beforeTop = mapTopLevelBands(beforeBands);
    const afterTop = mapTopLevelBands(afterBands);
    const identities = new Set([...beforeTop.keys(), ...afterTop.keys()]);
    const added = [];
    const removed = [];
    const modified = [];
    const reordered = [];

    identities.forEach(identity => {
        const beforeEntry = beforeTop.get(identity);
        const afterEntry = afterTop.get(identity);

        if (!beforeEntry && afterEntry) {
            added.push(afterEntry.label);
            return;
        }
        if (beforeEntry && !afterEntry) {
            removed.push(beforeEntry.label);
            return;
        }
        if (!beforeEntry || !afterEntry) return;

        if (bandContentEqual(beforeEntry.band, afterEntry.band)) return;

        if (isCombChildOrderOnlyChange(beforeEntry.band, afterEntry.band)) {
            reordered.push(beforeEntry.label || afterEntry.label);
            return;
        }

        modified.push(
            describeModifiedBand(beforeEntry.band, afterEntry.band, afterEntry.label || beforeEntry.label)
        );
    });

    return {
        added,
        removed,
        modified,
        reordered,
        topLevelOrderChanged: detectTopLevelBandOrderChanged(beforeBands, afterBands)
    };
}

function hasNonOrderBandChanges(summary) {
    if (!summary) return false;
    return !!(
        summary.added?.length ||
        summary.removed?.length ||
        summary.modified?.length ||
        summary.reordered?.length
    );
}

/** Choose which bands to show in compare panels based on change type. */
export function buildSmartCompareDisplayBands(beforeBands, afterBands, summary) {
    if (summary?.topLevelOrderChanged) {
        return {
            beforeBands,
            afterBands
        };
    }

    const filtered = filterExecutiveSummaryBandsToChangedOnly(beforeBands, afterBands);
    return {
        beforeBands: filtered.beforeBands,
        afterBands: filtered.afterBands
    };
}

/** Parse, diff-filter, and normalize bands for the log compare modal. */
export function prepareExecutiveSummaryLogCompareData(changesetValues) {
    const { beforeBands, afterBands } = parseExecutiveSummaryLogChangeset(changesetValues);
    const baseSummary = summarizeExecutiveSummaryLogDiff(beforeBands, afterBands);
    const summary = {
        ...baseSummary,
        mixedOrderAndContent: Boolean(baseSummary.topLevelOrderChanged && hasNonOrderBandChanges(baseSummary))
    };
    const { beforeBands: displayBefore, afterBands: displayAfter } = buildSmartCompareDisplayBands(
        beforeBands,
        afterBands,
        summary
    );

    return {
        beforeBands: normalizeExecutiveSummaryBandsForDisplay(displayBefore),
        afterBands: normalizeExecutiveSummaryBandsForDisplay(displayAfter),
        summary
    };
}

/** Build expanded-band map (all bands open) for the compare modal. */
export function buildExecutiveSummaryLogExpandedKeys(bands) {
    const rowKeys = collectExecutiveSummaryRowKeys(bands);
    return rowKeys.reduce((acc, key) => {
        acc[key] = true;
        return acc;
    }, {});
}

/** Extract executive_summary changeset values from a log item (main or associated). */
export function getExecutiveSummaryChangesetFromLogItem(item) {
    if (!item) return null;
    if (item.changeset?.executive_summary) {
        return item.changeset.executive_summary;
    }
    if (Array.isArray(item.associated_changes)) {
        for (const change of item.associated_changes) {
            if (change?.changeset?.executive_summary) {
                return change.changeset.executive_summary;
            }
        }
    }
    return null;
}
